import type { WinePhoto } from "@corklog/shared";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  deleteWinePhoto,
  designateWinePhoto,
  uploadWinePhoto,
} from "../api.js";
import { useMayWrite } from "../cellar.js";
import { resizeForUpload, UnreadableImage } from "../photos.js";
import { actionButton } from "./controls.js";

/**
 * Photographs of the bottle, on the wine's own page.
 *
 * **What the photograph is for is recognising a bottle**, and the second use,
 * which turned out to be the better one, is reading a back label: importer,
 * alcohol and sometimes the disgorgement date are printed there and nowhere
 * else this application can reach. That is why the full size is 1600px and why
 * a photograph opens rather than only sitting in a column — a back label is
 * legible across a screen and illegible beside a paragraph.
 *
 * **Not in the wine list**, deliberately: `design-direction.md` gives the
 * drinking-window band the one loud place in that list, and a picture on every
 * row would take it. That is an amendment to the design document if it is ever
 * wanted, not a change to this component.
 */
export function WinePhotos({
  cellarId,
  wineId,
  photos,
  onChanged,
}: {
  cellarId: string;
  wineId: string;
  photos: readonly WinePhoto[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const mayWrite = useMayWrite();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<WinePhoto | null>(null);

  // Escape closes the enlargement. Found by driving it: the overlay claims
  // `role="dialog"` and `aria-modal`, and a dialog that cannot be dismissed
  // from the keyboard is a promise the markup makes and the behaviour breaks.
  // Tapping the backdrop is the phone gesture and stays; this is the other one.
  useEffect(() => {
    if (opened === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpened(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened]);

  // A guest sees the photographs and cannot change them. Nothing is hidden:
  // a photograph of the bottle is the cellar's wine rather than the running of
  // it, which is the line the guest decision draws.
  if (photos.length === 0 && mayWrite === false) return null;

  async function choose(file: File) {
    setBusy(true);
    setError(null);
    try {
      const sizes = await resizeForUpload(file);
      await uploadWinePhoto(cellarId, wineId, {
        ...sizes,
        designate: photos.length === 0,
      });
      onChanged();
    } catch (cause) {
      // Three failures worth telling apart, because *try again* is useless
      // advice for two of them.
      //
      // The unreadable one is almost always an iPhone's HEIC arriving whole.
      // The unavailable one is this application admitting there is nowhere to
      // put a photograph yet — no bucket, which is true in production until
      // one is made, and a button that fails silently there would be the thing
      // `project-scope.md` refuses when it says a feature with no service
      // behind it should be dark rather than broken.
      if (cause instanceof UnreadableImage) setError(t("photos.unreadable"));
      else if (cause instanceof ApiError && cause.code === "photo_store_unavailable")
        setError(t("photos.unavailable"));
      else if (cause instanceof ApiError && cause.code === "photo_too_large")
        setError(t("photos.tooLarge"));
      else setError(t("photos.failed"));
    } finally {
      setBusy(false);
      if (fileInput.current !== null) fileInput.current.value = "";
    }
  }

  return (
    <section className="mt-8">
      <h2 className="font-display text-ink text-lg">{t("photos.heading")}</h2>

      {photos.length === 0 ? (
        <p className="text-muted mt-2 text-sm">{t("photos.none")}</p>
      ) : (
        <ul className="mt-3 flex flex-wrap items-start gap-3">
          {photos.map((photo) => (
            // The thumbnail is the whole photograph, not a square cut from its
            // middle. It was `h-40 w-40 object-cover` once, and a bottle
            // photographed upright lost its neck and its foot to it — what was
            // left was the label, when the point of photographing the bottle
            // rather than the label is the capsule, the shape and the glass.
            // A fixed height and the width the photograph's own proportions
            // give it keeps a row of them level; a 3:4 box with `object-contain`
            // was the alternative, and letterboxes any photograph taken the
            // other way round.
            <li key={photo.id} className="max-w-full">
              <button
                type="button"
                className="border-line block max-w-full overflow-hidden rounded border"
                onClick={() => setOpened(photo)}
              >
                <img
                  src={photo.smallUrl}
                  alt={t("photos.alt")}
                  loading="lazy"
                  className="block h-60 w-auto max-w-full object-contain"
                />
              </button>
              {mayWrite === true && (
                <div className="mt-1 flex items-center justify-between gap-2 text-sm">
                  {photo.isDesignated ? (
                    <span className="text-muted">{t("photos.designated")}</span>
                  ) : (
                    <button
                      type="button"
                      className="text-accent"
                      disabled={busy}
                      onClick={() => {
                        setBusy(true);
                        void designateWinePhoto(cellarId, wineId, photo.id)
                          .then(onChanged)
                          .catch(() => setError(t("photos.failed")))
                          .finally(() => setBusy(false));
                      }}
                    >
                      {t("photos.designate")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-muted"
                    disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      void deleteWinePhoto(cellarId, wineId, photo.id)
                        .then(onChanged)
                        .catch(() => setError(t("photos.failed")))
                        .finally(() => setBusy(false));
                    }}
                  >
                    {t("photos.remove")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {mayWrite === true && (
        <div className="mt-3">
          {/* One input, not two modes. On a desktop this is the file picker;
              on a phone the browser offers the camera and the library side by
              side, which is the whole requirement. `capture` is deliberately
              absent: it reads like the right attribute and forces the camera,
              removing the library — exactly half of what was asked for. */}
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) void choose(file);
            }}
          />
          <button
            type="button"
            className={actionButton}
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {busy ? t("photos.adding") : t("photos.add")}
          </button>
        </div>
      )}

      {error !== null && <p className="text-accent mt-2 text-sm">{error}</p>}

      {opened !== null && (
        // Full width and full height, because reading a back label is the
        // reason the larger size is stored at all.
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t("photos.opened")}
          onClick={() => setOpened(null)}
        >
          <img
            src={opened.url}
            alt={t("photos.alt")}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      )}
    </section>
  );
}
